import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    const updateCartCount = () => {
      const count = parseInt(localStorage.getItem('cart_count') || '0');
      setCartCount(count);
    };
    
    updateCartCount();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  const scrollToSection = (id) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    }
  };

  const goToCart = () => {
    navigate('/cart');
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-[#0A0A0F]/95 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20' : 'bg-transparent'}`}>
      <div className="max-w-[1400px] mx-auto px-[7.6923%]">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer group" onClick={() => navigate('/')}>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              CLOUD{' '}
              <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent group-hover:from-[#60A5FA] group-hover:to-[#A78BFA] transition-all duration-300">
                IT
              </span>
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {['Home', 'About', 'Services', 'CRM', 'Portfolio', 'Contact'].map((item) => (
              <button 
                key={item}
                data-testid={`nav-${item.toLowerCase()}`}
                onClick={() => {
                  if (item === 'Portfolio') navigate('/portfolio');
                  else if (item === 'CRM') navigate('/crm/pricing');
                  else scrollToSection(item.toLowerCase());
                }} 
                className={`relative text-white/60 hover:text-white transition-colors duration-300 text-[15px] font-medium group ${item === 'CRM' ? 'text-blue-400 hover:text-blue-300' : ''}`}
              >
                {item}
                {item === 'CRM' && (
                  <span className="absolute -top-2 -right-3 px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full">NEW</span>
                )}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
            
            {/* Cart Icon */}
            <button
              data-testid="cart-btn"
              onClick={goToCart}
              className="relative text-white/60 hover:text-[#F97316] transition-colors duration-300"
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-[#F97316] to-[#FB923C] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </button>
            
            {/* User Menu */}
            {user ? (
              <div className="flex items-center gap-4">
                <button
                  data-testid="dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300 font-medium group"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                  <span className="group-hover:text-[#3B82F6] transition-colors">{user.name?.split(' ')[0]}</span>
                </button>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate('/'); }}
                  className="text-white/50 hover:text-red-400 transition-colors duration-300"
                  title="Logout"
                >
                  <LogOut size={20} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  data-testid="login-btn"
                  onClick={() => navigate('/login')}
                  className="text-white/80 hover:text-white transition-colors duration-300 font-medium px-4 py-2"
                >
                  Login
                </button>
                <button 
                  data-testid="signup-btn"
                  onClick={() => navigate('/signup')}
                  className="relative px-6 py-2.5 font-semibold text-white rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#3B82F6]/25"
                  style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                >
                  Sign Up
                </button>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            {/* Mobile Cart Icon */}
            <button
              onClick={goToCart}
              className="relative text-white hover:text-[#F97316] transition-colors duration-300"
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-[#F97316] to-[#FB923C] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </button>
            
            <button 
              className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#0A0A0F]/98 backdrop-blur-xl border-t border-white/10 py-6 rounded-b-2xl">
            <nav className="flex flex-col gap-2 px-2">
              {['Home', 'About', 'Services', 'CRM', 'Portfolio', 'Contact'].map((item) => (
                <button 
                  key={item}
                  onClick={() => {
                    if (item === 'Portfolio') { navigate('/portfolio'); setIsMobileMenuOpen(false); }
                    else if (item === 'CRM') { navigate('/crm/pricing'); setIsMobileMenuOpen(false); }
                    else scrollToSection(item.toLowerCase());
                  }} 
                  className={`text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300 text-lg text-left py-3 px-4 rounded-xl flex items-center justify-between ${item === 'CRM' ? 'text-blue-400' : ''}`}
                >
                  {item}
                  {item === 'CRM' && (
                    <span className="px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full">NEW</span>
                  )}
                </button>
              ))}
              
              <div className="h-px bg-white/10 my-4"></div>
              
              {user ? (
                <>
                  <button
                    onClick={() => { navigate('/dashboard'); setIsMobileMenuOpen(false); }}
                    className="flex items-center gap-3 text-white hover:bg-white/5 transition-all duration-300 text-lg text-left py-3 px-4 rounded-xl"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center">
                      <User size={16} className="text-white" />
                    </div>
                    Dashboard
                  </button>
                  <button
                    onClick={() => { logout(); navigate('/'); setIsMobileMenuOpen(false); }}
                    className="flex items-center gap-3 text-red-400 hover:bg-red-500/10 transition-all duration-300 text-lg text-left py-3 px-4 rounded-xl"
                  >
                    <LogOut size={20} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}
                    className="text-white hover:bg-white/5 transition-all duration-300 text-lg text-left py-3 px-4 rounded-xl border border-white/10"
                  >
                    Login
                  </button>
                  <button 
                    onClick={() => { navigate('/signup'); setIsMobileMenuOpen(false); }}
                    className="text-white font-semibold transition-all duration-300 text-lg text-left py-3 px-4 rounded-xl"
                    style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                  >
                    Sign Up Free
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
